import { createListenerMiddleware } from '@reduxjs/toolkit';

import { trackLead, trackLogin, trackCompleteRegistration, trackOnce } from '@/utils/analytics';
import { pulsarEvent, pulsarIdentify, pulsarLogout } from '@/utils/analytics/pulsar';

import {
  authenticationLoginEmail,
  authenticationLoginPassword,
  authenticationLink,
  authenticationSsoGoogle,
  authenticationSsoTelegram,
  authenticationSsoTelegramComplete,
} from './slices/authentication';
import type { RootState } from './index';

/**
 * Fires the no-amount, state-driven standardized conversion events (Lead, Login,
 * CompleteRegistration) from reliable redux boundaries — mirrors the other listener
 * middlewares in `store/index.ts`. The amount-bearing paid-funnel events
 * (begin_checkout / add_payment_info / purchase / subscribe) fire from the configurator
 * components, where the billing total + currency live.
 *
 * Each milestone is reported to TWO sinks, side by side and sharing the same fire-once guard:
 *   - the consent-gated marketing trackers (`trackLead` / … → GA4 + FB + Reddit), which no-op
 *     without marketing consent (their SDKs only exist once consent loaded them); and
 *   - the first-party Pulsar beacon (`pulsarEvent('<event_type>')`), which is NOT consent-gated
 *     but self-guards on its own config (no endpoint / prerender → no-op). The Pulsar `event_type`
 *     mirrors the GA4 event name (generate_lead / login / sign_up). The beacon auto-attaches the
 *     authenticated identity + page, so only opaque, non-PII props are passed.
 * Firing from here is therefore safe regardless of either gate.
 */
export const analyticsConversionsListener = createListenerMiddleware();

type AuthIdentityPayload = {
  token?: unknown;
  session_id?: unknown;
  user?: { user_id?: unknown } | null;
};

// Keep this outside reportLead's trackOnce guard: repeated leads and identity switches must still
// update the beacon identity before any conversion is built.
const identifyAuthenticatedEvent = (data: AuthIdentityPayload | null | undefined): boolean => {
  if (typeof data?.token !== 'string' || !data.token) return false;
  if (typeof data.user?.user_id !== 'string' || !data.user.user_id) return false;

  const sessionId = typeof data.session_id === 'string' && data.session_id ? data.session_id : undefined;
  // identify() only overwrites provided fields, so clear an old account session before applying a
  // valid token-bearing identity whose authoritative response omitted the optional session id.
  if (!sessionId) pulsarLogout();
  pulsarIdentify({ user_id: data.user.user_id, ...(sessionId ? { session_id: sessionId } : {}) });
  return true;
};

/** Lead: a NEW owner account, once per user (a retry or a second tab must not re-count).
 *  Returns false when this browser already counted that user. */
const reportLead = (userId: string | undefined): boolean =>
  trackOnce(`lead:${userId ?? 'new'}`, () => {
    trackLead();
    pulsarEvent('generate_lead');
  });

/** Login: an EXISTING owner completed sign-in through `method`. */
const reportLogin = (method: 'magic_link' | 'password' | 'google' | 'telegram'): void => {
  trackLogin(method);
  pulsarEvent('login', { method });
};

// Lead — a NEW owner submitted their email and was let into the configurator. The backend
// auto-registers new accounts and returns a token (`payload.data.success === true`); an
// existing account instead gets a login link (`false`) and is NOT a new lead.
analyticsConversionsListener.startListening({
  actionCreator: authenticationLoginEmail.fulfilled,
  effect: (action) => {
    const data = action.payload?.data;
    if (!data?.success || !identifyAuthenticatedEvent(data)) return;
    reportLead(data.user.user_id);
  },
});

// Login — a returning owner completed magic-link sign-in.
analyticsConversionsListener.startListening({
  actionCreator: authenticationLink.fulfilled,
  effect: (action) => {
    if (action.payload?.success && identifyAuthenticatedEvent(action.payload.data)) reportLogin('magic_link');
  },
});

analyticsConversionsListener.startListening({
  actionCreator: authenticationLoginPassword.fulfilled,
  effect: (action) => {
    if (action.payload?.success && identifyAuthenticatedEvent(action.payload.data)) reportLogin('password');
  },
});

// SSO (Google / Telegram). Only a token-bearing result is a sign-in: `linkSent` and Telegram's
// `needsEmail` intermediate carry no token and report nothing.
//
// The SSO LoginResponse has NO new-vs-existing flag, so each route reports what the backend
// contract (micros-wl_owner_hub sso.md → "Flows") makes certain:
//   - `/sso/telegram` logs in a KNOWN identity only (an unknown one gets `needsEmail`) → Login.
//   - `/sso/telegram/complete` logs in the account it just CREATED, always `email_verified: false`
//     → Lead. A verified account here is the rare "identity linked meanwhile" sign-in → Login.
//   - `/sso/google` returns the same payload for a known identity, a same-email attach and a new
//     account. Owner decision (2026-10-05): the FIRST Google sign-in of a user in this browser is
//     the Lead, every later one a Login. Over-counts a returning owner on a new browser, and one
//     who only ever signed in here by magic link or Telegram (those write no lead key); a backend
//     `created` flag on the response would make this exact.
analyticsConversionsListener.startListening({
  actionCreator: authenticationSsoGoogle.fulfilled,
  effect: (action) => {
    if (!action.payload.success || !identifyAuthenticatedEvent(action.payload.data)) return;
    if (!reportLead(action.payload.data.user.user_id)) reportLogin('google');
  },
});
analyticsConversionsListener.startListening({
  actionCreator: authenticationSsoTelegram.fulfilled,
  effect: (action) => {
    if (action.payload.success && identifyAuthenticatedEvent(action.payload.data)) reportLogin('telegram');
  },
});
analyticsConversionsListener.startListening({
  actionCreator: authenticationSsoTelegramComplete.fulfilled,
  effect: (action) => {
    if (!action.payload.success || !identifyAuthenticatedEvent(action.payload.data)) return;
    const { user } = action.payload.data;
    if (user.email_verified) reportLogin('telegram');
    else reportLead(user.user_id);
  },
});

// CompleteRegistration — post-purchase, the backend signals a verification email went out
// (`session.postPayment` → 'success-mail' | 'mail'), i.e. the "check your email" screen.
// Fired once per session (the WS can re-emit the signal on reconnect).
const MAIL_SENT = new Set<string>(['success-mail', 'mail']);
analyticsConversionsListener.startListening({
  predicate: (_action, currentState, previousState) => {
    const cur = (currentState as RootState).session.postPayment;
    const prev = (previousState as RootState).session.postPayment;
    return cur !== prev && cur != null && MAIL_SENT.has(cur);
  },
  effect: (_action, api) => {
    const sessionId = (api.getState() as RootState).session.sessionId ?? 'unknown';
    trackOnce(`${sessionId}:complete_registration`, () => {
      trackCompleteRegistration('email');
      pulsarEvent('sign_up', { method: 'email' });
    });
  },
});
